import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ScrollText,
  Search,
  Shield,
  UserPlus,
  UserMinus,
  FileCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  Filter,
  Activity,
  Lock,
} from "lucide-react";
import { listAuditLogs } from "./lifecycleApi";

const ENTITY_FILTERS = [
  { value: "", label: "All", icon: Activity },
  { value: "onboarding", label: "Onboarding", icon: UserPlus },
  { value: "offboarding", label: "Offboarding", icon: UserMinus },
  { value: "onboarding_approval", label: "Approvals", icon: FileCheck },
];

const ENTITY_META = {
  onboarding: { label: "Onboarding", icon: UserPlus, color: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  offboarding: { label: "Offboarding", icon: UserMinus, color: "text-rose-700", bg: "bg-rose-50", ring: "ring-rose-200" },
  onboarding_approval: { label: "Approval", icon: FileCheck, color: "text-amber-700", bg: "bg-amber-50", ring: "ring-amber-200" },
  onboarding_document: { label: "Document", icon: ScrollText, color: "text-blue-700", bg: "bg-blue-50", ring: "ring-blue-200" },
};

const ACTION_STYLES = {
  created: "bg-sky-100 text-sky-800",
  exit_completed: "bg-rose-100 text-rose-800",
  submitted_for_approval: "bg-violet-100 text-violet-800",
  employee_activated: "bg-emerald-100 text-emerald-800",
  approval_approve: "bg-green-100 text-green-800",
  approval_reject: "bg-red-100 text-red-800",
};

const ROLE_STYLES = {
  admin: "bg-indigo-100 text-indigo-800",
  hr: "bg-purple-100 text-purple-800",
  manager: "bg-blue-100 text-blue-800",
  employee: "bg-slate-100 text-slate-700",
};

function formatAction(action = "") {
  return action
    .replace(/^approval_/, "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isUuid(value) {
  return typeof value === "string" && /^[0-9a-f-]{36}$/i.test(value);
}

function formatValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return null;
  const str = String(value);
  if (isUuid(str)) return null;
  return str;
}

const HIDDEN_CHANGE_KEYS = new Set([
  "employee_id", "entity_id", "onboarding_id", "offboarding_id",
  "actor_id", "approver_id", "document_id", "approval_id", "document_url",
]);

function visibleChanges(data) {
  if (!data) return [];
  return Object.entries(data).filter(([key, value]) => {
    if (HIDDEN_CHANGE_KEYS.has(key) || key.endsWith("_id")) return false;
    return formatValue(value) !== null;
  });
}

function relativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function groupByDate(items) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups = { Today: [], Yesterday: [], Earlier: [] };
  items.forEach((log) => {
    const d = new Date(log.created_at);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) groups.Today.push(log);
    else if (d.getTime() === yesterday.getTime()) groups.Yesterday.push(log);
    else groups.Earlier.push(log);
  });
  return groups;
}

function ChangeBlock({ label, data, variant }) {
  const entries = visibleChanges(data);
  if (!entries.length) return null;
  const isBefore = variant === "before";
  return (
    <div className={`rounded-lg p-3 ${isBefore ? "bg-slate-50 border border-slate-200" : "bg-violet-50 border border-violet-100"}`}>
      <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${isBefore ? "text-slate-500" : "text-violet-600"}`}>
        {label}
      </p>
      <dl className="space-y-1.5">
        {entries.map(([key, value]) => (
          <div key={key} className="flex flex-wrap gap-x-2 text-xs">
            <dt className="text-slate-500 shrink-0">{formatKey(key)}</dt>
            <dd className="font-medium text-slate-800 break-all">{formatValue(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function AuditEntry({ log, expanded, onToggle }) {
  const meta = ENTITY_META[log.entity_type] || {
    label: log.entity_type,
    icon: ScrollText,
    color: "text-slate-700",
    bg: "bg-slate-50",
    ring: "ring-slate-200",
  };
  const Icon = meta.icon;
  const actionStyle = ACTION_STYLES[log.action] || "bg-slate-100 text-slate-700";
  const roleStyle = ROLE_STYLES[log.actor_role?.toLowerCase()] || "bg-slate-100 text-slate-600";
  const previewChanges = visibleChanges(log.new_value);
  const hasDetails = visibleChanges(log.previous_value).length > 0 || previewChanges.length > 0;

  return (
    <article
      className="group relative rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      style={{ borderColor: "var(--slate-200, #e2e8f0)" }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-violet-400 to-violet-600 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className={`p-2.5 rounded-xl ring-1 ${meta.bg} ${meta.ring}`}>
            <Icon className={`w-4 h-4 ${meta.color}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${actionStyle}`}>
                {formatAction(log.action)}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${meta.bg} ${meta.color}`}>
                {meta.label}
              </span>
              {log.actor_role && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${roleStyle}`}>
                  <Shield className="w-3 h-3" />
                  {log.actor_role}
                </span>
              )}
            </div>

            <p className="text-sm font-medium text-slate-700">
              {log.entity_label || meta.label}
            </p>
            {log.actor_name && (
              <p className="text-xs text-slate-500 mt-0.5">By {log.actor_name}</p>
            )}

            {!expanded && previewChanges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {previewChanges.slice(0, 3).map(([key, value]) => (
                  <span key={key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-50 border border-slate-100 text-[11px] text-slate-600">
                    <span className="text-slate-400">{formatKey(key)}:</span>
                    <span className="font-medium">{formatValue(value)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="text-right shrink-0">
            <p className="text-xs font-medium text-slate-700">{relativeTime(log.created_at)}</p>
            <p className="text-[10px] text-slate-400 mt-0.5 flex items-center justify-end gap-1">
              <Clock className="w-3 h-3" />
              {new Date(log.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        {hasDetails && (
          <button
            type="button"
            onClick={onToggle}
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-800 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? "Hide details" : "View change details"}
          </button>
        )}

        {expanded && hasDetails && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-slate-100">
            <ChangeBlock label="Before" data={log.previous_value} variant="before" />
            <ChangeBlock label="After" data={log.new_value} variant="after" />
          </div>
        )}
      </div>
    </article>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="rounded-xl border p-4 shadow-sm"
      style={{ background: "var(--bg-surface, #fff)", borderColor: "var(--slate-200, #e2e8f0)" }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
          <p className="text-2xl font-bold mt-1 text-slate-800">{value}</p>
        </div>
        <div className={`p-2.5 rounded-xl ${accent}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border p-5 animate-pulse" style={{ borderColor: "var(--slate-200)" }}>
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-3 bg-slate-100 rounded w-2/3" />
          <div className="h-3 bg-slate-100 rounded w-1/2" />
        </div>
      </div>
    </div>
  );
}

export default function LcAuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [entityType, setEntityType] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAuditLogs({
        entity_type: entityType || undefined,
        page_size: 100,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((log) =>
      log.action?.toLowerCase().includes(q)
      || log.entity_type?.toLowerCase().includes(q)
      || log.entity_label?.toLowerCase().includes(q)
      || log.actor_name?.toLowerCase().includes(q)
      || log.actor_role?.toLowerCase().includes(q)
      || JSON.stringify(log.new_value || {}).toLowerCase().includes(q)
    );
  }, [items, search]);

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCount = items.filter((l) => new Date(l.created_at) >= today).length;
    const entities = new Set(items.map((l) => l.entity_type)).size;
    return { todayCount, entities };
  }, [items]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-violet-100">
              <ScrollText className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Audit Logs</h2>
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                <Lock className="w-3 h-3" />
                Immutable compliance trail · {total} total entries
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={ScrollText} label="Total Entries" value={total} accent="bg-violet-500" />
        <StatCard icon={Activity} label="Today" value={stats.todayCount} accent="bg-emerald-500" />
        <StatCard icon={Filter} label="Entity Types" value={stats.entities} accent="bg-sky-500" />
      </div>

      {/* Filters */}
      <div
        className="rounded-xl border p-4 space-y-4"
        style={{ background: "var(--bg-surface, #fff)", borderColor: "var(--slate-200, #e2e8f0)" }}
      >
        <div className="flex flex-wrap gap-2">
          {ENTITY_FILTERS.map(({ value, label, icon: Icon }) => (
            <button
              key={value || "all"}
              type="button"
              onClick={() => setEntityType(value)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                entityType === value
                  ? "bg-violet-600 text-white shadow-sm"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-violet-200 focus:border-violet-300 outline-none transition"
            placeholder="Search actions, roles, or changes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {search && (
          <p className="text-xs text-slate-500">
            Showing {filtered.length} of {items.length} loaded entries
          </p>
        )}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((n) => <SkeletonCard key={n} />)}
        </div>
      ) : !filtered.length ? (
        <div
          className="rounded-xl border py-16 text-center"
          style={{ background: "var(--bg-surface, #fff)", borderColor: "var(--slate-200)" }}
        >
          <div className="inline-flex p-4 rounded-2xl bg-slate-50 mb-4">
            <ScrollText className="w-8 h-8 text-slate-300" />
          </div>
          <p className="font-semibold text-slate-700">No audit logs found</p>
          <p className="text-sm text-slate-500 mt-1">
            {search || entityType ? "Try adjusting your filters" : "Activity will appear here as lifecycle events occur"}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([period, logs]) =>
            logs.length > 0 && (
              <section key={period}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                  <span className="h-px flex-1 bg-slate-200" />
                  {period}
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px]">{logs.length}</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </h3>
                <div className="space-y-3">
                  {logs.map((log) => (
                    <AuditEntry
                      key={log.audit_id}
                      log={log}
                      expanded={expandedId === log.audit_id}
                      onToggle={() => setExpandedId(expandedId === log.audit_id ? null : log.audit_id)}
                    />
                  ))}
                </div>
              </section>
            )
          )}
        </div>
      )}
    </div>
  );
}
